import { useCallback, useEffect, useState } from 'react';
import { paymentsGateway } from '../index';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { sandboxTransmissionActionsEnabled } from '../../../shared/commercialActions';
import { FeatureUnavailableNotice } from '../../../shared/components/FeatureUnavailableNotice';
import type { PaymentBatchResponse, PaymentTransactionResponse, PayrollRunSummary } from '../types';

export function PaymentsPage() {
  // EPN-98: no live ACH provider exists, so initiating a batch could only ever produce a simulated
  // "Submitted"/"Settled". Nothing is fetched or shown — see shared/commercialActions.ts.
  if (!sandboxTransmissionActionsEnabled) {
    return (
      <section className="card card--wide">
        <div className="section-header">
          <h2>Payments & Direct Deposit</h2>
        </div>
        <FeatureUnavailableNotice feature="Direct deposit (ACH) payments">
          EPN is not connected to a live payment provider in this release, so payment batches cannot be
          initiated here. No payment has been sent and no money has been moved by EPN.
        </FeatureUnavailableNotice>
      </section>
    );
  }

  return <PaymentsWorkspace />;
}

function PaymentsWorkspace() {
  const [runs, setRuns] = useState<PayrollRunSummary[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [batches, setBatches] = useState<PaymentBatchResponse[]>([]);
  const [transactions, setTransactions] = useState<Record<string, PaymentTransactionResponse[]>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRuns(await paymentsGateway.listApprovedPayrollRuns());
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load approved payroll runs.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBatches = useCallback(async (runId: string) => {
    setError(null);
    try {
      const list = await paymentsGateway.listBatchesForRun(runId);
      setBatches(list);
      const txnEntries = await Promise.all(list.map(async (b) => [b.id, await paymentsGateway.listTransactions(b.id)] as const));
      setTransactions(Object.fromEntries(txnEntries));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load payment batches.'));
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    if (selectedRunId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      void loadBatches(selectedRunId);
    }
  }, [selectedRunId, loadBatches]);

  async function handleInitiate() {
    if (!selectedRunId) return;
    setBusy(true);
    setError(null);
    try {
      await paymentsGateway.initiateBatch(selectedRunId);
      await loadBatches(selectedRunId);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not initiate the payment batch — one may already exist for this payroll run.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleCheckStatus(id: string) {
    setBusy(true);
    setError(null);
    try {
      await paymentsGateway.checkBatchStatus(id);
      if (selectedRunId) await loadBatches(selectedRunId);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not check the batch status.'));
    } finally {
      setBusy(false);
    }
  }

  const hasBatch = batches.length > 0;

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Payments & Direct Deposit</h2>
        <span className="badge badge--caution">Sandbox Simulation Only — No Live Money Movement</span>
      </div>
      <p className="muted">
        Mock ACH provider only — see <code>docs/payments-direct-deposit.md</code>. No bank-account field
        exists anywhere in this build, so no account information is shown; there is also no void/reversal
        control, since none exists in the backend (a batch is immutable once Settled or Failed).
      </p>

      {error && <p className="error">{error}</p>}
      {loading && <p className="muted">Loading approved payroll runs…</p>}
      {!loading && runs.length === 0 && <p className="muted">No Approved payroll runs yet.</p>}

      {!loading && runs.length > 0 && (
        <table>
          <thead><tr><th>Period</th><th>Check Date</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id}>
                <td>{r.periodStartDate} – {r.periodEndDate}</td>
                <td>{r.checkDate}</td>
                <td>{r.status}</td>
                <td>
                  <button type="button" className="link" onClick={() => setSelectedRunId(r.id)}>
                    {selectedRunId === r.id ? 'Selected' : 'Select'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedRunId && (
        <div className="section">
          <div className="section-header">
            <h3>Payment Batch — Payroll Run {selectedRunId}</h3>
            <button type="button" disabled={busy || hasBatch} onClick={() => void handleInitiate()}>
              {hasBatch ? 'Batch already initiated' : busy ? 'Initiating…' : 'Initiate payment batch'}
            </button>
          </div>

          {batches.map((b) => (
            <div key={b.id} style={{ marginBottom: 16 }}>
              <p>
                Status: <strong>{b.status}</strong>
                {b.status === 'Failed' && b.failureReason && <span className="error"> — {b.failureReason}</span>}
                {b.status === 'Submitted' && (
                  <> <button type="button" className="link" disabled={busy} onClick={() => void handleCheckStatus(b.id)}>Check status</button></>
                )}
              </p>
              <table>
                <thead><tr><th>Employee</th><th>Amount</th></tr></thead>
                <tbody>
                  {(transactions[b.id] ?? []).map((t) => (
                    <tr key={t.id}><td>{t.employeeId}</td><td>{t.amount.toFixed(2)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {batches.length === 0 && <p className="muted">No payment batch initiated yet for this run.</p>}
        </div>
      )}
    </section>
  );
}
